import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { User, WhatsappLogo, CheckCircle, Info, Lock, ArrowSquareOut } from '@phosphor-icons/react';
import { toast } from 'sonner';
import LoadingBar from '../components/LoadingBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);
  const [profileData, setProfileData] = useState({ name: '', whatsapp_number: '' });
  const [botInfo, setBotInfo] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, botRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/whatsapp/bot-info`, { withCredentials: true })
      ]);
      setUser(userRes.data);
      setBotInfo(botRes.data);
      setProfileData({ 
        name: userRes.data.name, 
        whatsapp_number: userRes.data.whatsapp_number || ''
      });
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${BACKEND_URL}/api/profile`, { name: profileData.name }, { withCredentials: true });
      toast.success('Perfil atualizado!');
      setUser({ ...user, name: profileData.name });
    } catch (error) {
      toast.error('Erro ao atualizar perfil');
    }
  };

  const handleUpdateWhatsapp = async () => {
    if (!profileData.whatsapp_number) {
      toast.error('Digite um número de WhatsApp');
      return;
    }
    setSavingWhatsapp(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/profile`, 
        { whatsapp_number: profileData.whatsapp_number }, 
        { withCredentials: true }
      );
      toast.success('WhatsApp vinculado com sucesso!');
      setUser({ ...user, whatsapp_number: profileData.whatsapp_number });
    } catch (error) {
      toast.error('Erro ao vincular WhatsApp');
    } finally {
      setSavingWhatsapp(false);
    }
  };

  if (loading) return <LoadingBar text="Carregando perfil..." />;

  return (
    <div className="p-3 sm:p-6 md:p-8 space-y-8" data-testid="profile-page">
      <div className="flex items-center gap-3 sm:gap-4 mb-2">
        <Avatar className="w-12 h-12 sm:w-16 sm:h-16 border-2 border-muted">
          <AvatarFallback className="text-xl sm:text-2xl bg-primary/10">
            {user?.name ? user.name.charAt(0).toUpperCase() : <User size={32} />}
          </AvatarFallback>
        </Avatar>
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">{user?.name || 'Perfil'}</h1>
          <p className="text-muted-foreground">{user?.email}</p>
        </div>
      </div>

      {/* Profile Info */}
      <Card>
        <CardHeader>
          <CardTitle>Informações Pessoais</CardTitle>
          <CardDescription>Atualize seus dados pessoais</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpdateProfile} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input 
                  id="name"
                  value={profileData.name} 
                  onChange={(e) => setProfileData({...profileData, name: e.target.value})} 
                  data-testid="profile-name" 
                  placeholder="Seu nome"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email"
                  value={user?.email} 
                  disabled 
                  className="bg-muted" 
                />
                <p className="text-xs text-muted-foreground">O email não pode ser alterado</p>
              </div>
            </div>
            <Button type="submit" data-testid="update-profile-button">
              Salvar Alterações
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* WhatsApp Integration Card */}
      <Card className="border-green-200 bg-green-50/30">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500 rounded-lg">
              <WhatsappLogo size={24} className="text-white" weight="fill" />
            </div>
            <div>
              <CardTitle className="text-green-800">WhatsApp Bot</CardTitle>
              <CardDescription>Crie tarefas e receba lembretes via WhatsApp</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {user?.whatsapp_number ? (
            <>
              <div className="flex items-center gap-2 p-3 bg-green-100 rounded-lg border border-green-300">
                <CheckCircle size={20} className="text-green-600" weight="fill" />
                <span className="text-green-800 font-medium">
                  WhatsApp vinculado: {user.whatsapp_number}
                </span>
              </div>
              
              {/* Locked number notice */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <Lock size={16} className="text-gray-400" />
                <span className="text-gray-500 text-xs">
                  Para alterar o número, entre em contato: <a href="mailto:suporte@aretesystem.com.br" className="text-primary underline">suporte@aretesystem.com.br</a>
                </span>
              </div>

              {/* wa.me link to start conversation */}
              {botInfo?.bot_number && (
                <a
                  href={`https://wa.me/${botInfo.bot_number}?text=${encodeURIComponent(botInfo.onboarding_message)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full p-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                  data-testid="whatsapp-start-link"
                >
                  <WhatsappLogo size={20} weight="fill" />
                  Iniciar conversa com a Aretê
                  <ArrowSquareOut size={16} />
                </a>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <Info size={20} className="text-amber-600" />
                <span className="text-amber-800 text-sm">
                  Vincule seu WhatsApp para usar o bot
                </span>
              </div>

              <div className="space-y-2">
                <Label htmlFor="whatsapp">Número do WhatsApp</Label>
                <div className="flex gap-2">
                  <Input 
                    id="whatsapp"
                    value={profileData.whatsapp_number} 
                    onChange={(e) => setProfileData({...profileData, whatsapp_number: e.target.value})} 
                    placeholder="Ex: 11999999999"
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleUpdateWhatsapp}
                    disabled={savingWhatsapp}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {savingWhatsapp ? 'Salvando...' : 'Vincular'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Digite apenas números com DDD (sem +55). Cada número pode ser vinculado a apenas uma conta.
                </p>
              </div>
            </>
          )}

          <div className="p-4 bg-white rounded-lg border border-green-200 space-y-3">
            <h4 className="font-medium text-green-800">O que você pode fazer:</h4>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span><strong>Criar tarefas:</strong> "Me lembra de pagar a conta amanhã às 10h"</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span><strong>Ver tarefas:</strong> Digite "tarefas hoje"</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span><strong>Resumo:</strong> Digite "resumo" para ver estatísticas</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span><strong>Concluir:</strong> Digite "concluir [nome da tarefa]"</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span><strong>Lembretes automáticos</strong> antes dos compromissos</span>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
